// React and related imports
import React, { useState } from 'react';

// Flowbite components
import { Button, Modal } from 'flowbite-react';

// Interfaces and Enums
import { IMedia } from '../../common/interfaces/Media.interface';
import { MediaTypes } from '../../common/enums/MediaTypes';

// Custom Components
import { MediaGalleryToolbar } from './MediaGalleryToolbar';
import { MediaGalleryGrid } from './MediaGalleryGrid';
import { MediaPreview } from './MediaPreview';

// i18 hooks
import { useTranslation } from 'react-i18next';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

type MediaGalleryModalProps = {
  show: boolean;
  onClose: () => void;
  onSelect: (media: IMedia | null) => void;
  onSubmit: () => void;
  selectedMedia?: IMedia;
  mediaItems: IMedia[];
};

export const MediaGalleryModal: React.FC<MediaGalleryModalProps> = ({
  show,
  onClose,
  onSelect,
  onSubmit,
  selectedMedia,
  mediaItems,
}) => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  const [previewItem, setPreviewItem] = useState<IMedia | null>(null);

  const [filter, setFilter] = useState<'all' | MediaTypes>('all');

  const filteredMedia = mediaItems.filter((item) =>
    filter === 'all' ? true : item.type === filter
  );

  return (
    <div>
      <Modal show={show} onClose={onClose}>
        <Modal.Header className="modal-header !p-3">
          <div className="w-full">
            <MediaGalleryToolbar
              filter={filter}
              onFilterChange={setFilter}
              totalItems={mediaItems.length}
            />
          </div>
        </Modal.Header>
        <Modal.Body className="flex-grow overflow-auto !p-0">
          <MediaGalleryGrid
            items={filteredMedia}
            selectedMedia={selectedMedia}
            onSelect={onSelect}
            onPreview={setPreviewItem}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            className={`${buttonClass} me-4`}
            onClick={onClose}
          >
            {t('content.cancel')}
          </Button>
          <Button type="button" className={buttonClass} onClick={onSubmit}>
            {t('content.submit')}
          </Button>
        </Modal.Footer>
      </Modal>

      <MediaPreview
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        onSelect={() => {
          if (previewItem) {
            onSelect(previewItem);
            setPreviewItem(null);
          }
        }}
      />
    </div>
  );
};
